import { useEffect, useMemo, useRef, useState } from "react";
import {
  ArrowLeftIcon,
  ArrowRightIcon,
  CheckCircle2Icon,
  CheckIcon,
  CircleIcon,
  KeyIcon,
  Loader2Icon,
  PenLineIcon,
  PlusIcon,
  RefreshCwIcon,
  RocketIcon,
  SendIcon,
  XCircleIcon,
  XIcon,
} from "lucide-react";
import { toast } from "sonner";
import type { Address, Hex } from "viem";
import { MAX_UINT256 } from "@woodpecker/chain";
import {
  DEPLOY_STEPS,
  PRESET_PRICING,
  SERVICE_ID_RE,
  TOKEN_SYMBOL_RE,
  isHttpUrl,
  sameAccountWorkersDev,
  workersDomainOf,
  type DeployForm,
  type DeployStep,
  type PriceRule,
  type StepState,
} from "@woodpecker/protocol";
import { Button, Badge } from "../ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "../ui/card";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../ui/select";
import { Input, Label, Textarea } from "../ui/form";
import { cn } from "../lib/cn";
import { round2, sum, summaryOfRule, priceText, batchText, maxOf, short } from "../lib/format";
import { ICONS, KIND_ICON, ServiceIcon, isIcon } from "../lib/icons";
import { useN, useRich, useT, useTT, Y, type TextRef } from "../i18n";
import { useStore } from "../store/store";
import { useWallet, type SignLine } from "../store/wallet";
import { fingerprintOf, putUpstreamKey, type ConversationView } from "../adapters/clients";
import type { ServiceEntry } from "../adapters/chain";
import { LabelTag, Rejected, Waiting, useBuy } from "../components/common";
import { useLiveConversation } from "../components/hooks";
import { capsText, GAS, useSignLines } from "../components/signing";
import { navigate } from "../components/Shell";
import { Transcript } from "./Conversation";
import { modelsOf } from "./Conversations";

/** The presets of the deploy form (D36): the key's shape, the kinds, the default upstream and the suggested names. */
const PRESETS: Record<
  string,
  {
    perCredit: number;
    shape?: string;
    kinds: Array<"model" | "tool" | "memory" | "store">;
    models?: string[];
    route: string;
    tool?: string;
    noKey?: boolean;
    upstream?: string;
    suggest: { id: string; symbol: string; label: string };
  }
> = {
  "openai-compatible": {
    perCredit: 4,
    shape: "sk-…",
    kinds: ["model"],
    models: ["gpt-6-luna", "gpt-5.6-luna"],
    route: "POST /v1/chat/completions",
    upstream: "https://api.openai.com/v1",
    suggest: { id: "gpt-test", symbol: "tGptTest", label: "label.gptTest" },
  },
  "elevenlabs-tts": {
    perCredit: 2,
    shape: "sk_…",
    kinds: ["tool"],
    route: "POST /v1/text-to-speech",
    tool: "speak",
    upstream: "https://api.elevenlabs.io",
    suggest: { id: "voice-test", symbol: "tVoiceTest", label: "label.voiceTest" },
  },
  "apify-run": {
    perCredit: 5,
    shape: "apify_api_…",
    kinds: ["tool"],
    route: "POST /v2/acts/run",
    tool: "run_actor",
    upstream: "https://api.apify.com",
    suggest: { id: "apify-test", symbol: "tApifyTest", label: "label.apifyTest" },
  },
  "jev-decide": {
    perCredit: 2,
    shape: "jev_…",
    kinds: ["tool"],
    route: "POST /decide",
    tool: "decide",
    upstream: "https://api.jev.example",
    suggest: { id: "decide-test", symbol: "tDecideTest", label: "label.decideTest" },
  },
  "generic-rest": {
    perCredit: 1,
    kinds: ["tool"],
    route: "POST /call",
    tool: "call",
    upstream: "",
    suggest: { id: "rest-test", symbol: "tRestTest", label: "label.restTest" },
  },
  memory: {
    perCredit: 2,
    kinds: ["memory"],
    route: "POST /commit",
    noKey: true,
    suggest: { id: "memory-test", symbol: "tMemoryTest", label: "label.memoryTest" },
  },
  store: {
    perCredit: 2,
    kinds: ["store"],
    route: "PUT /files/<name>",
    noKey: true,
    suggest: { id: "store-test", symbol: "tStoreTest", label: "label.storeTest" },
  },
  other: {
    perCredit: 1,
    kinds: ["tool"],
    route: "GET /forecast",
    tool: "forecast",
    upstream: "",
    suggest: { id: "api-test", symbol: "tApiTest", label: "label.otherTest" },
  },
};
const PRESET_NAMES = Object.keys(PRESETS);
const needsKey = (preset: string | null) => !preset || !PRESETS[preset]?.noKey;
const detectPreset = (key: string) => {
  const k = key.trim();
  return k.startsWith("sk-")
    ? "openai-compatible"
    : k.startsWith("sk_") || /^[0-9a-f]{32}$/i.test(k)
      ? "elevenlabs-tts"
      : k.startsWith("apify_api_")
        ? "apify-run"
        : k.startsWith("jev_")
          ? "jev-decide"
          : "other";
};
const pricingOf = (preset: string): PriceRule => PRESET_PRICING[preset] ?? { form: "fixed", price: 1 };
const validPricing = (p: PriceRule) => {
  const ok = (n: number) => Number.isInteger(n) && n >= 1;
  if (p.form === "fixed") return ok(p.price);
  if (p.form === "metered") return p.per > 0 && ok(p.maxPerCall);
  return (
    p.tiers.length > 0 &&
    p.tiers.every(([l, pr], i) => ok(l) && ok(pr) && (i === 0 || l > p.tiers[i - 1]![0])) &&
    (p.measure === "bytes" || p.path.trim() !== "")
  );
};
const CURRENT_KEY = "woodpecker-deploy-current";
const labelsOf = (s: string) => {
  const seen = new Set<string>();
  return s
    .split(",")
    .map((l) => l.replace(/\s+/g, " ").trim())
    .filter((l) => l && !seen.has(l.toLowerCase()) && seen.add(l.toLowerCase()));
};

interface Draft {
  preset: string | null;
  detected: string | null;
  keyFingerprint: string | null;
  deploymentId: string;
  id: string;
  symbol: string;
  labels: string;
  icon: string;
  pricing: PriceRule | null;
  rate: number; // wp per token
  batch: number | "turn";
  upstreamUrl: string;
  models: string;
  handle: string;
}

function Prereqs({ runner, model, host }: { runner?: ServiceEntry; model?: { display: string }; host?: ServiceEntry }) {
  const t = useT();
  const row = (label: string, ok: boolean, text: string) => (
    <li className="flex items-center gap-3 text-sm">
      {ok ? (
        <CheckCircle2Icon className="size-4 shrink-0 text-emerald-600 dark:text-emerald-400" />
      ) : (
        <XCircleIcon className="size-4 shrink-0 text-destructive" />
      )}
      <span className="w-16 text-muted-foreground">{label}</span>
      {ok ? (
        <span className="font-mono text-xs">{text}</span>
      ) : (
        <span className="text-xs">
          {text}{" "}
          <a className="font-medium underline" href="#/services">
            {t("common.add")}
          </a>
        </span>
      )}
    </li>
  );
  return (
    <Card size="sm">
      <CardHeader>
        <CardTitle>{t("deploy.prereq.title")}</CardTitle>
        <CardDescription>{t("deploy.prereq.description")}</CardDescription>
      </CardHeader>
      <CardContent>
        <ul className="flex flex-col gap-2">
          {row(t("kind.runner"), !!runner, runner?.name ?? t("deploy.prereq.noRunner"))}
          {row(t("kind.model"), !!model, model?.display ?? t("deploy.prereq.noModel"))}
          {row(t("kind.host"), !!host, host?.name ?? t("deploy.prereq.noCloud"))}
        </ul>
      </CardContent>
    </Card>
  );
}

function Field({
  id,
  label,
  value,
  placeholder,
  disabled,
  problem,
  hint,
  onChange,
}: {
  id: string;
  label: string;
  value: string;
  placeholder?: string;
  disabled?: boolean;
  problem?: TextRef | string;
  hint?: React.ReactNode;
  onChange: (v: string) => void;
}) {
  const tt = useTT();
  return (
    <div className="grid min-w-0 content-start gap-2">
      <Label htmlFor={id}>{label}</Label>
      <Input
        id={id}
        autoComplete="off"
        spellCheck={false}
        value={value}
        placeholder={placeholder}
        disabled={disabled}
        aria-invalid={!!problem}
        onChange={(e) => onChange(e.target.value)}
      />
      {problem ? (
        <p className="text-xs text-destructive">{tt(problem)}</p>
      ) : (
        hint && <p className="text-xs text-muted-foreground">{hint}</p>
      )}
    </div>
  );
}

function NumberInput({
  value,
  onChange,
  className,
  ...rest
}: { value: number; onChange: (n: number) => void; className?: string } & Omit<
  React.ComponentProps<typeof Input>,
  "value" | "onChange"
>) {
  const [text, setText] = useState(String(value));
  return (
    <Input
      {...rest}
      inputMode="decimal"
      autoComplete="off"
      className={cn("w-24 tabular-nums", className)}
      value={text}
      onChange={(e) => {
        setText(e.target.value);
        onChange(e.target.value.trim() === "" ? NaN : Number(e.target.value));
      }}
    />
  );
}

function PricingEditor({
  draft,
  set,
  disabled,
}: {
  draft: Draft;
  set: (p: Partial<Draft>) => void;
  disabled: boolean;
}) {
  const t = useT();
  const tt = useTT();
  const [editing, setEditing] = useState(false);
  const [rateText, setRateText] = useState<string | null>(null);
  const p = draft.pricing;
  const symbol = draft.symbol || "—";
  const summary = p ? summaryOfRule(p, draft.preset === "memory" ? "commit" : undefined) : null;
  const max = summary ? maxOf(summary) : 0;
  const perCredit = draft.rate > 0 ? round2(1 / draft.rate) : 0;
  const setP = (next: PriceRule) => set({ pricing: next });
  return (
    <div className="grid gap-3 sm:grid-cols-[minmax(0,1fr)_auto] sm:items-start">
      <div className="grid min-w-0 gap-2">
        <Label>{t("deploy.pricing.title")}</Label>
        <div className="flex min-h-9 flex-wrap items-center gap-x-2 gap-y-1 text-sm">
          {p && summary ? (
            <>
              <span className="font-mono text-xs">
                {tt(priceText(summary, symbol))}{" "}
                <span className="text-muted-foreground">· {t("common.wp", { n: round2(max * draft.rate) })}</span>
              </span>
              {!disabled && (
                <Button variant="link" size="sm" className="h-auto px-0" onClick={() => setEditing((e) => !e)}>
                  {t(editing ? "deploy.pricing.done" : "deploy.pricing.edit")}
                </Button>
              )}
            </>
          ) : (
            <span className="text-muted-foreground">{t("deploy.details.presetPending")}</span>
          )}
        </div>
        {p && !validPricing(p) && <p className="text-xs text-destructive">{t("deploy.pricing.invalid")}</p>}
      </div>
      <div className="grid gap-2">
        <Label htmlFor="deploy-rate">{t("deploy.pricing.rate")}</Label>
        <div className="flex items-center gap-2 text-sm">
          <span className="font-mono text-xs whitespace-nowrap">1 wpCredit =</span>
          <Input
            id="deploy-rate"
            inputMode="decimal"
            autoComplete="off"
            className="w-20 tabular-nums"
            value={rateText ?? (p ? String(perCredit) : "")}
            disabled={!p || disabled}
            aria-invalid={!(draft.rate > 0 && draft.rate <= 1000)}
            onChange={(e) => {
              setRateText(e.target.value);
              const v = Number(e.target.value);
              set({ rate: e.target.value.trim() === "" || !(v > 0) ? NaN : 1 / v });
            }}
          />
          <span className="font-mono text-xs">{symbol}</span>
        </div>
        {p && !(draft.rate > 0 && draft.rate <= 1000) && (
          <p className="text-xs text-destructive">{t("deploy.pricing.rateInvalid")}</p>
        )}
      </div>
      {editing && !disabled && p && (
        <div className="grid gap-3 rounded-lg border bg-muted/30 p-3 sm:col-span-2" key={`${draft.preset}-${p.form}`}>
          <div className="flex flex-wrap items-center gap-3">
            <Label htmlFor="pricing-form" className="w-24">
              {t("deploy.pricing.form")}
            </Label>
            <Select
              value={p.form}
              onValueChange={(f) => {
                const base = pricingOf(draft.preset ?? "");
                setP(
                  base.form === f
                    ? base
                    : f === "fixed"
                      ? { form: "fixed", price: 1 }
                      : f === "tiered"
                        ? {
                            form: "tiered",
                            measure: "length",
                            path: "$.text",
                            tiers: [
                              [500, 1],
                              [2000, 3],
                            ],
                          }
                        : { form: "metered", meter: "openai-usage", per: 1000, maxPerCall: 100 },
                );
              }}
            >
              <SelectTrigger id="pricing-form" className="w-40 font-mono">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {["fixed", "tiered", "metered"].map((f) => (
                  <SelectItem key={f} value={f} className="font-mono">
                    {f}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          {p.form === "fixed" && (
            <div className="flex flex-wrap items-center gap-3">
              <Label htmlFor="pricing-price" className="w-24">
                {t("deploy.pricing.price")}
              </Label>
              <NumberInput id="pricing-price" value={p.price} onChange={(v) => setP({ ...p, price: v })} />
              <span className="font-mono text-xs text-muted-foreground">{symbol}</span>
            </div>
          )}
          {p.form === "tiered" && (
            <>
              <div className="flex flex-wrap items-center gap-3">
                <Label htmlFor="pricing-measure" className="w-24">
                  {t("deploy.pricing.measure")}
                </Label>
                <Select value={p.measure} onValueChange={(m) => setP({ ...p, measure: m as typeof p.measure })}>
                  <SelectTrigger id="pricing-measure" className="w-52 max-w-full">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {(["number", "length", "codepoints", "bytes"] as const).map((m) => (
                      <SelectItem key={m} value={m}>
                        {t(`measureOpt.${m}`)}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                {p.measure !== "bytes" && (
                  <Input
                    aria-label={t("deploy.pricing.path")}
                    placeholder="$.text"
                    className="w-36 font-mono"
                    value={p.path}
                    onChange={(e) => setP({ ...p, path: e.target.value })}
                  />
                )}
              </div>
              <div className="grid gap-2">
                <span className="text-xs text-muted-foreground">{t("deploy.pricing.tiers")}</span>
                <ol className="flex flex-col gap-1.5">
                  {p.tiers.map(([limit, price], i) => (
                    <li key={i} className="flex flex-wrap items-center gap-2 text-sm">
                      <span className="w-3 text-xs text-muted-foreground">≤</span>
                      <NumberInput
                        aria-label={t("deploy.pricing.limit")}
                        className="w-20"
                        value={limit}
                        onChange={(v) =>
                          setP({ ...p, tiers: p.tiers.map((tier, j) => (j === i ? [v, tier[1]] : tier)) })
                        }
                      />
                      <span className="text-muted-foreground">→</span>
                      <NumberInput
                        aria-label={t("deploy.pricing.price")}
                        className="w-14"
                        value={price}
                        onChange={(v) =>
                          setP({ ...p, tiers: p.tiers.map((tier, j) => (j === i ? [tier[0], v] : tier)) })
                        }
                      />
                      <span className="font-mono text-xs text-muted-foreground">{symbol}</span>
                      {p.tiers.length > 1 && (
                        <Button
                          variant="ghost"
                          size="icon-sm"
                          aria-label={t("deploy.pricing.removeTier")}
                          onClick={() => setP({ ...p, tiers: p.tiers.filter((_, j) => j !== i) })}
                        >
                          <XIcon />
                        </Button>
                      )}
                    </li>
                  ))}
                </ol>
                <Button
                  variant="outline"
                  size="sm"
                  className="w-fit"
                  onClick={() => {
                    const [l, pr] = p.tiers[p.tiers.length - 1] ?? [0, 0];
                    setP({ ...p, tiers: [...p.tiers, [Math.max(1, l * 2), pr + 1]] });
                  }}
                >
                  <PlusIcon />
                  {t("deploy.pricing.addTier")}
                </Button>
              </div>
            </>
          )}
          {p.form === "metered" && (
            <>
              <div className="flex flex-wrap items-center gap-3">
                <Label htmlFor="pricing-meter" className="w-24">
                  {t("deploy.pricing.meter")}
                </Label>
                <Select value={p.meter} onValueChange={(m) => setP({ ...p, meter: m as typeof p.meter })}>
                  <SelectTrigger id="pricing-meter" className="w-44 font-mono">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {(["openai-usage", "apify-usage", "bytes-tiers"] as const).map((m) => (
                      <SelectItem key={m} value={m} className="font-mono">
                        {m}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="flex flex-wrap items-center gap-2 text-sm">
                <span className="w-24 shrink-0 text-xs text-muted-foreground">{t("deploy.pricing.per")}</span>
                <span className="font-mono text-xs">1 {symbol} /</span>
                <NumberInput
                  aria-label={t("deploy.pricing.per")}
                  value={p.per}
                  onChange={(v) => setP({ ...p, per: v })}
                />
                <span className="text-xs text-muted-foreground">{t(`meterUnit.${p.meter}`)}</span>
              </div>
              <div className="flex flex-wrap items-center gap-3">
                <Label htmlFor="pricing-max" className="w-24 font-mono">
                  maxPerCall
                </Label>
                <NumberInput id="pricing-max" value={p.maxPerCall} onChange={(v) => setP({ ...p, maxPerCall: v })} />
                <span className="font-mono text-xs text-muted-foreground">{symbol}</span>
              </div>
            </>
          )}
          <div className="flex flex-wrap gap-2">
            <Button size="sm" onClick={() => setEditing(false)}>
              {t("deploy.pricing.done")}
            </Button>
            <Button size="sm" variant="ghost" onClick={() => set({ pricing: pricingOf(draft.preset ?? "") })}>
              {t("deploy.pricing.reset")}
            </Button>
          </div>
        </div>
      )}
    </div>
  );
}

function BatchField({ draft, set, disabled }: { draft: Draft; set: (p: Partial<Draft>) => void; disabled: boolean }) {
  const t = useT();
  const [n, setN] = useState(draft.batch !== 1 && draft.batch !== "turn");
  const mode = draft.batch === "turn" ? "turn" : n || draft.batch !== 1 ? "n" : "one";
  return (
    <div className="grid gap-2">
      <Label htmlFor="deploy-batch">{t("deploy.batch.title")}</Label>
      <div className="flex flex-wrap items-center gap-2">
        <Select
          value={mode}
          disabled={disabled}
          onValueChange={(m) => {
            setN(m === "n");
            set({ batch: m === "turn" ? "turn" : m === "n" ? 8 : 1 });
          }}
        >
          <SelectTrigger id="deploy-batch" className="w-56">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="one">{t("deploy.batch.one")}</SelectItem>
            <SelectItem value="n">{t("deploy.batch.n")}</SelectItem>
          </SelectContent>
        </Select>
        {mode === "n" && typeof draft.batch === "number" && (
          <NumberInput
            aria-label={t("deploy.batch.count")}
            className="w-20"
            value={draft.batch}
            disabled={disabled}
            onChange={(v) => set({ batch: v })}
          />
        )}
      </div>
      {!(draft.batch === "turn" || (Number.isInteger(draft.batch) && (draft.batch as number) >= 1)) && (
        <p className="text-xs text-destructive">{t("deploy.batch.invalid")}</p>
      )}
      <p className="text-xs text-muted-foreground">{t("deploy.batch.help")}</p>
    </div>
  );
}

const STEP_ICON = ({ state }: { state: StepState }) =>
  state === "done" ? (
    <CheckCircle2Icon className="size-4 text-emerald-600 dark:text-emerald-400" />
  ) : state === "running" ? (
    <Loader2Icon className="size-4 animate-spin" />
  ) : state === "needs signature" ? (
    <PenLineIcon className="size-4 text-amber-600 dark:text-amber-400" />
  ) : state === "failed" ? (
    <XCircleIcon className="size-4 text-destructive" />
  ) : (
    <CircleIcon className="size-4 text-muted-foreground/50" />
  );

function Progress({ states, preset }: { states: Record<string, StepState>; preset: string | null }) {
  const t = useT();
  const steps = needsKey(preset) ? DEPLOY_STEPS : DEPLOY_STEPS.filter((s) => s !== "check-key" && s !== "set-key");
  return (
    <Card size="sm" className="h-fit">
      <CardHeader>
        <CardTitle>{t("deploy.progress")}</CardTitle>
      </CardHeader>
      <CardContent>
        <ol className="flex flex-col gap-2">
          {steps.map((s: DeployStep) => {
            const st = states[s] ?? "pending";
            return (
              <li key={s} className="flex items-center gap-2.5 text-sm">
                <STEP_ICON state={st} />
                <span className={cn(st === "pending" && "text-muted-foreground")}>{t(`step.${s}`)}</span>
                {st === "needs signature" && (
                  <span className="text-xs text-amber-600 dark:text-amber-400">{t("deploy.needsSignature")}</span>
                )}
                {st === "failed" && <span className="text-xs text-destructive">{t("deploy.failed")}</span>}
              </li>
            );
          })}
        </ol>
      </CardContent>
    </Card>
  );
}

/**
 * The deployment's wallet cards (D30): when the DO opens one, the Console asks the wallet for its lines and tells the
 * DO the result. Used by the deploy page and by the conversation view of a deployment.
 */
export function DeployCards({ c }: { c: ConversationView }) {
  const { chain, wallet, config, runner, setConversation, refreshDirectory } = useStore();
  const { request } = useWallet();
  const lines = useSignLines();
  const t = useT();
  const asked = useRef<string | null>(null);
  const d = c.deploy;
  const card = d?.card;
  const key =
    card && card.state === "pending"
      ? `${c.id}:${card.name}:${c.messages.filter((m) => m.role === "signature").length}`
      : null;
  useEffect(() => {
    if (!key || !card || !d || !wallet || !runner || asked.current === key) return;
    asked.current = key;
    let token: Address | undefined = d.tokenAddress;
    const sigLines: SignLine[] = [];
    for (const l of card.lines) {
      if (l.call) continue;
      const a = l.args ?? {};
      switch (l.kind) {
        case "fund":
          sigLines.push(lines.sendEth(a.to as Address, a.amount ?? "0.0001", d.serviceName));
          break;
        case "deployToken":
          sigLines.push(
            lines.raw(
              { plain: Y("wallet.plain.deployToken", { symbol: a.symbol ?? "" }), text: l.text, gas: GAS.deployToken },
              async () => {
                const r = await chain.deployToken(
                  wallet.address,
                  a.name ?? a.symbol ?? "",
                  a.symbol ?? "",
                  BigInt(a.rate ?? "1000000"),
                );
                token = r.address;
                return { tx: r.tx };
              },
            ),
          );
          break;
        case "setService":
          sigLines.push(
            lines.raw(
              { plain: Y("wallet.plain.setService", { symbol: d.form.symbol }), text: l.text, gas: GAS.setService },
              async () => ({ tx: (await chain.setService(wallet.address, token!, a.payTo as Address)).tx }),
            ),
          );
          break;
        case "setMintAllowance":
          sigLines.push(
            lines.raw(
              {
                plain: Y("wallet.plain.mintAllowance", { symbol: d.form.symbol }),
                text: l.text,
                address: config.contractA,
                gas: GAS.setService,
              },
              async () => ({
                tx: (await chain.setMintAllowance(wallet.address, token!, config.contractA, MAX_UINT256)).tx,
              }),
            ),
          );
          break;
        case "claim":
          sigLines.push(
            lines.raw(
              { plain: Y("wallet.plain.claim"), text: l.text, address: config.directory, gas: GAS.register },
              async () => ({ tx: (await chain.claim(wallet.address, a.name ?? "")).tx }),
            ),
          );
          break;
        case "set":
          sigLines.push(
            lines.raw(
              {
                plain: Y("wallet.plain.register", { name: d.serviceName }),
                text: l.text,
                address: config.directory,
                gas: GAS.register,
              },
              async () => ({ tx: (await chain.set(wallet.address, token!, a.serviceName ?? "", a.url ?? "")).tx }),
            ),
          );
          break;
        case "setPricingHash":
          sigLines.push(
            lines.raw(
              {
                plain: Y("wallet.plain.setPricingHash", { name: d.serviceName }),
                text: l.text,
                address: config.directory,
                gas: GAS.register,
              },
              async () => ({
                tx: (await chain.setPricingHash(wallet.address, (a.token as Address) || token!, a.hash as Hex)).tx,
              }),
            ),
          );
          break;
        case "mint":
          sigLines.push(
            lines.raw(
              {
                plain: Y("wallet.req.mintSelf", { amount: Number(a.amount ?? 10), symbol: d.form.symbol }),
                text: l.text,
                address: a.token,
                gas: GAS.mint,
              },
              async () => ({
                tx: (await chain.mint(wallet.address, a.token as Address, wallet.address, BigInt(a.amount ?? "10"))).tx,
              }),
            ),
          );
          break;
        case "approve":
          sigLines.push(
            lines.raw(
              {
                plain: Y("wallet.plain.approve", { token: d.form.symbol }),
                text: l.text,
                address: config.contractA,
                gas: GAS.approve,
              },
              async () => ({
                tx: (await chain.approve(wallet.address, a.token as Address, config.contractA, MAX_UINT256)).tx,
              }),
            ),
          );
          break;
        case "authorize":
          sigLines.push(
            lines.raw(
              {
                plain: Y("wallet.plain.authorizeTest", { cap: `${a.cap ?? "1"} ${d.form.symbol}` }),
                text: l.text,
                address: a.identity,
                gas: GAS.authorize,
              },
              async () => ({
                tx: (
                  await chain.authorize(wallet.address, a.identity as Address, a.token as Address, BigInt(a.cap ?? "1"))
                ).tx,
              }),
            ),
          );
          break;
        default:
          break;
      }
    }
    const kinds = card.lines.filter((l) => !l.call).map((l) => l.kind ?? "");
    request({
      title:
        card.name === "contracts"
          ? Y("wallet.req.contracts", { symbol: d.form.symbol, id: d.form.id })
          : Y(d.form.preset === "memory" ? "wallet.req.verifyMemory" : "wallet.req.verify", { id: d.form.id }),
      note: card.name === "contracts" ? Y("wallet.req.nTransactions", { n: sigLines.length }) : undefined,
      tokens: card.name === "verify" ? [{ amount: 1, symbol: d.form.symbol }] : undefined,
      lines: sigLines,
      onLine: (i, s) => {
        setConversation({
          ...c,
          messages: c.messages.map((m) =>
            m.role === "signature" && m.id === `sig-${card.name}`
              ? {
                  ...m,
                  signatures: (m.signatures ?? []).map((sl, j) =>
                    j === i
                      ? {
                          ...sl,
                          state: s.state === "signing" ? "signing" : s.state === "signed" ? "signed" : "pending",
                          tx: s.tx,
                        }
                      : sl,
                  ),
                }
              : m,
          ),
        });
      },
      onDone: async (r, ls) => {
        if (r !== "signed") {
          await runner.card(c.id, card.name, { rejected: true }).catch(() => undefined);
          toast.error(t("deploy.rejectedToast"));
          setConversation(await runner.get(c.id));
          return;
        }
        const txs = ls.map((l, i) => ({ kind: kinds[i] ?? "", tx: l.tx ?? "" }));
        await runner
          .card(c.id, card.name, { signed: true, txs, ...(card.name === "contracts" ? { tokenAddress: token } : {}) })
          .catch((e: Error) => toast.error(e.message));
        toast.success(t("deploy.cardSigned"));
        if (card.name === "contracts") await refreshDirectory();
        setConversation(await runner.get(c.id));
      },
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [key]);
  if (!d) return null;
  return null;
}

function RunPanel({
  c,
  draft,
  caps,
  ctx,
}: {
  c: ConversationView | undefined;
  draft: Draft;
  caps: Record<string, number>;
  ctx: Ctx | null;
}) {
  const { runner, setConversation } = useStore();
  const t = useT();
  const rich = useRich();
  const [note, setNote] = useState("");
  const d = c?.deploy;
  const states: Record<string, StepState> = d ? d.steps : { details: "pending" };
  const running = c?.status === "running" && !d?.card;
  // the agent stopped: a rejected card (d.error) or a step that paused with its reason; Retry runs it again
  const paused = c?.status === "paused" ? c.pause : undefined;
  const failed = (!!d?.error || !!paused) && c?.status !== "running";
  const runningText = running ? (d?.card ? t("script.contracts.running") : t("conv.runningPlain")) : null;
  return (
    <div className="grid gap-4 lg:grid-cols-[minmax(0,1fr)_240px]">
      <Card className="min-w-0 gap-0 py-0">
        <div className="flex flex-wrap items-center justify-between gap-x-3 gap-y-1 border-b px-4 py-3">
          <p className="text-sm">
            {rich("deploy.header", {
              runner: <span className="font-mono">{ctx?.runner.name ?? "—"}</span>,
              identity: <span className="font-mono">{c ? short(c.agent) : "—"}</span>,
              caps: <span className="font-mono">{ctx ? capsText(caps) : "—"}</span>,
            })}
          </p>
          {c && (
            <span className="inline-flex items-center gap-1.5 text-sm text-emerald-600 dark:text-emerald-400">
              <CheckIcon className="size-4" />
              {t("deploy.signed")}{" "}
              <a className="underline" href={`#/conversations/${c.id}`}>
                {t("deploy.openConversation")}
              </a>
            </span>
          )}
        </div>
        <Transcript
          messages={c?.messages ?? []}
          running={runningText ?? undefined}
          className="lg:max-h-[36rem] lg:overflow-y-auto"
          empty={t("deploy.empty.start")}
        />
        {c && <DeployCards c={c} />}
        {c?.status === "done" ? (
          <div className="flex flex-wrap items-center justify-between gap-3 border-t p-3 text-sm">
            <span className="inline-flex items-center gap-1.5 text-emerald-600 dark:text-emerald-400">
              <CheckCircle2Icon className="size-4" />
              {t("deploy.done")}
            </span>
            <div className="flex flex-wrap gap-2">
              <Button variant="outline" size="sm" asChild>
                <a href={`#/conversations/${c.id}`}>
                  {t("deploy.openConv")}
                  <ArrowRightIcon />
                </a>
              </Button>
              <Button
                variant="outline"
                size="sm"
                onClick={() => {
                  localStorage.removeItem(CURRENT_KEY);
                  window.location.reload();
                }}
              >
                <RefreshCwIcon />
                {t("deploy.another")}
              </Button>
            </div>
          </div>
        ) : (
          <div className="flex flex-wrap items-end gap-2 border-t p-3">
            {paused && (
              <p className="w-full text-xs text-amber-700 dark:text-amber-400">
                {t("deploy.paused", { detail: paused.detail ?? paused.reason })}
              </p>
            )}
            <Textarea
              rows={2}
              className="min-h-0 min-w-48 flex-1 resize-none"
              placeholder={t(
                !c
                  ? "deploy.placeholder.before"
                  : d?.card
                    ? "deploy.placeholder.sign"
                    : failed
                      ? "deploy.placeholder.failed"
                      : "deploy.placeholder.running",
              )}
              value={note}
              disabled={!c}
              onChange={(e) => setNote(e.target.value)}
            />
            {failed && c && (
              <Button
                variant="outline"
                onClick={async () => {
                  if (!runner) return;
                  await runner.retry(c.id).catch((e: Error) => toast.error(e.message));
                  setConversation(await runner.get(c.id));
                }}
              >
                <RefreshCwIcon />
                {t("deploy.retry")}
              </Button>
            )}
            <Button
              size="icon-lg"
              disabled={!c || !note.trim()}
              aria-label={t("common.send")}
              onClick={() => {
                setNote("");
                toast(t("deploy.noteToast"));
              }}
            >
              <SendIcon />
            </Button>
          </div>
        )}
      </Card>
      <Progress states={states} preset={d?.form.preset ?? draft.preset} />
    </div>
  );
}

interface Ctx {
  runner: ServiceEntry;
  model: { id: string; gateway: string; model: string; token: string; display: string; entry: ServiceEntry };
  host: ServiceEntry;
  memory: ServiceEntry;
}

export function DeployPage() {
  const {
    myServices,
    directory,
    settings,
    approvedTokens,
    balances,
    wallet,
    runner,
    config,
    refreshAuth,
    refreshConversations,
    conversations,
    wpPerToken,
    chain,
    refreshDirectory,
  } = useStore();
  const { request } = useWallet();
  const lines = useSignLines();
  const t = useT();
  const tt = useTT();
  const rich = useRich();
  const n = useN();
  const { onBuy } = useBuy();
  const host =
    directory.find((e) => e.name === `${config.provider}/cf`) ?? myServices.find((e) => e.kinds.includes("host"));
  const ctx = useMemo<Ctx | null>(() => {
    const runners = myServices.filter((e) => e.kinds.includes("runner"));
    const models = modelsOf(myServices);
    const memories = myServices.filter((e) => e.kinds.includes("memory"));
    const r = runners.find((e) => e.name === settings.defaultRunner) ?? runners[0];
    const m = models.find((x) => x.id === settings.defaultModel) ?? models[0];
    const mem =
      memories.find((e) => e.name === settings.defaultMemory) ??
      memories[0] ??
      directory.find((e) => e.name === `${config.provider}/memory`);
    return r && m && host && mem ? { runner: r, model: m, host, memory: mem } : null;
  }, [myServices, settings, host, directory, config.provider]);
  const [currentId, setCurrentId] = useState<string | null>(() => localStorage.getItem(CURRENT_KEY));
  const live = useLiveConversation(currentId);
  const current = live ?? conversations.find((c) => c.id === currentId);
  const [draft, setDraftState] = useState<Draft>({
    preset: null,
    detected: null,
    keyFingerprint: null,
    deploymentId: `d-${Math.random().toString(36).slice(2, 10)}`,
    id: "",
    symbol: "",
    labels: "",
    icon: "",
    pricing: null,
    rate: 1,
    batch: 1,
    upstreamUrl: "",
    models: "",
    handle: "",
  });
  const set = (p: Partial<Draft>) => setDraftState((d) => ({ ...d, ...p }));
  const [keyText, setKeyText] = useState("");
  const [storing, setStoring] = useState(false);
  const [capState, setCapState] = useState<"idle" | "waiting" | "rejected">("idle");
  const [handle, setHandle] = useState<{ name: string; exists: boolean } | null>(null);
  const registeredToken = current?.deploy?.tokenAddress ?? null;
  const currentStatus = current?.status ?? null;
  useEffect(() => {
    if (!wallet) return;
    chain
      .dirHandleOf(wallet.address)
      .then((h) => setHandle({ name: h.name, exists: h.exists }))
      .catch(() => setHandle(null));
  }, [chain, wallet, registeredToken]);
  // the contracts card registered the service and the run bound its token: the directory (My services) shows it
  useEffect(() => {
    if (registeredToken || currentStatus === "done") void refreshDirectory();
  }, [registeredToken, currentStatus, refreshDirectory]);
  const preset = draft.preset;
  const spec = preset ? PRESETS[preset] : undefined;
  const suggest = spec?.suggest;
  const details =
    preset && spec && suggest
      ? {
          ...draft,
          id: draft.id || suggest.id,
          symbol: draft.symbol || suggest.symbol,
          labels: draft.labels || t(suggest.label),
          pricing: draft.pricing ?? pricingOf(preset),
          models: draft.models || (spec.models ?? []).join(", "),
          upstreamUrl: draft.upstreamUrl || (spec.upstream ?? ""),
        }
      : null;
  const accountDomains = [
    ...new Set(
      [host, ...directory.filter((e) => e.name.startsWith(`${config.provider}/`))].flatMap((e) =>
        e ? (workersDomainOf(e.origin) ?? []) : [],
      ),
    ),
  ];
  const handleName = handle?.exists ? handle.name : draft.handle;
  const addr4 = wallet ? wallet.address.slice(2, 6).toLowerCase() : "";
  const fullName = details ? `${handleName || "…"}@${addr4}/${details.id}` : "";
  const problems: Record<string, TextRef> = {};
  if (details) {
    if (!SERVICE_ID_RE.test(details.id)) problems.id = Y("deploy.details.idInvalid");
    else if (!current && directory.some((e) => e.name === fullName))
      problems.id = Y("deploy.details.idTaken", { name: fullName });
    if (!TOKEN_SYMBOL_RE.test(details.symbol)) problems.symbol = Y("deploy.details.symbolInvalid");
    else if (!current && directory.some((e) => e.token === details.symbol))
      problems.symbol = Y("deploy.details.symbolTaken", { symbol: details.symbol });
    const labels = labelsOf(details.labels);
    if (labels.length < 1 || labels.length > 5 || labels.some((l) => l.length > 40))
      problems.labels = Y("deploy.details.labelInvalid");
    if (details.icon.trim() && !isIcon(details.icon.trim())) problems.icon = Y("deploy.details.iconInvalid");
    if (!handle?.exists && !/^[a-z][a-z0-9-]{2,15}$/.test(draft.handle)) problems.handle = Y("deploy.handleInvalid");
    if (needsKey(preset) && details.upstreamUrl) {
      // a Worker's fetch handler cannot reach a Worker of the same account on workers.dev (Cloudflare error 1042):
      // the account's domain is the one of the provider's Workers (the host service first)
      const sameAccount = accountDomains.find((d) => sameAccountWorkersDev(details.upstreamUrl, d));
      if (!isHttpUrl(details.upstreamUrl)) problems.upstreamUrl = Y("deploy.upstream.invalid");
      else if (sameAccount) problems.upstreamUrl = Y("deploy.upstream.sameAccount", { domain: sameAccount });
    }
  }
  const tokensNeeded = ctx ? [...new Set([ctx.runner.token, ctx.model.token, ctx.host.token, ctx.memory.token])] : [];
  const caps: Record<string, number> = {};
  for (const tok of tokensNeeded) {
    const min = tok === ctx?.host.token ? (needsKey(preset) ? 5 : 4) : tok === ctx?.model.token ? 7 : 3;
    caps[tok] = Math.max(
      settings.defaultCaps[tok] ??
        directory.find((e) => e.token === tok && e.defaultCap !== undefined)?.defaultCap ??
        5,
      min,
    );
  }
  const approveNeeded = tokensNeeded.filter((tok) => !approvedTokens.has(tok));
  // the tokens whose balance is below the deployment's cap: Deploy stays disabled and the hint below names them
  const shortfalls = ctx && details && !current ? tokensNeeded.filter((tok) => (balances[tok] ?? 0) < caps[tok]!) : [];
  const ready =
    !!ctx &&
    !!details &&
    (!!draft.keyFingerprint || !needsKey(preset)) &&
    Object.keys(problems).length === 0 &&
    validPricing(details.pricing!) &&
    draft.rate > 0 &&
    draft.rate <= 1000 &&
    !current &&
    capState !== "waiting" &&
    tokensNeeded.every((tok) => (balances[tok] ?? 0) >= caps[tok]!);
  const locked = !!current || capState === "waiting";

  async function storeKey() {
    const v = keyText.trim();
    if (!v || !wallet || !host) return;
    setStoring(true);
    try {
      const r = await putUpstreamKey(host.origin, host.name, wallet.account, draft.deploymentId, v);
      const detected = detectPreset(v);
      set({ keyFingerprint: r.fingerprint, detected, preset: draft.preset ?? detected });
      setKeyText("");
      toast.success(t("deploy.key.toast"));
    } catch (e) {
      toast.error(t("deploy.keyStoreFailed", { detail: e instanceof Error ? e.message : String(e) }));
    } finally {
      setStoring(false);
    }
  }

  async function deploy() {
    if (!ctx || !details || !ready || !runner || !wallet) return;
    setCapState("waiting");
    const form: DeployForm = {
      preset: details.preset!,
      id: details.id,
      symbol: details.symbol,
      labels: labelsOf(details.labels),
      icon: details.icon.trim() || undefined,
      pricing: details.pricing!,
      rate: String(Math.round(details.rate * 1e6)),
      batch: details.batch,
      models: details.models
        ? details.models
            .split(",")
            .map((m) => m.trim())
            .filter(Boolean)
        : undefined,
      upstreamUrl: details.upstreamUrl || undefined,
      handle: handle?.exists ? undefined : draft.handle,
    };
    let created: { conversationId: string; agent: Address };
    try {
      created = await runner.createDeployment({
        form,
        key: { fingerprint: draft.keyFingerprint, deployment: draft.deploymentId },
        model: ctx.model.id,
        memory: ctx.memory.name,
        host: ctx.host.name,
        caps,
      });
    } catch (e) {
      setCapState("idle");
      toast.error(t("deploy.createFailed", { detail: e instanceof Error ? e.message : String(e) }));
      return;
    }
    request({
      title: Y("wallet.req.allowDeployment"),
      amount: sum(...tokensNeeded.map((tok) => caps[tok]! * wpPerToken(tok))),
      tokens: tokensNeeded.map((symbol) => ({ amount: caps[symbol]!, symbol })),
      note: Y("wallet.req.forDeploying", { id: details.id }),
      lines: [
        ...approveNeeded.map((tok) => lines.approve(tok)),
        lines.authorize(Y("wallet.plain.authorizeDeployment", { caps: capsText(caps) }), created.agent, caps),
      ],
      onDone: async (r) => {
        if (r !== "signed") {
          setCapState("rejected");
          await refreshConversations();
          return;
        }
        setCapState("idle");
        await refreshAuth(created.conversationId, created.agent);
        await runner.card(created.conversationId, "caps", { signed: true }).catch((e: Error) => toast.error(e.message));
        localStorage.setItem(CURRENT_KEY, created.conversationId);
        setCurrentId(created.conversationId);
        await refreshConversations();
      },
    });
  }

  const shown = current?.deploy?.form;
  return (
    <>
      <div className="mb-4 flex flex-col gap-2">
        <a
          href="#/services"
          className="inline-flex w-fit items-center gap-1 text-sm text-muted-foreground hover:text-foreground"
        >
          <ArrowLeftIcon className="size-4" />
          {t("deploy.back")}
        </a>
        <h1 className="text-2xl font-semibold tracking-tight">{t("deploy.title")}</h1>
        <p className="text-sm text-muted-foreground">{t("deploy.description")}</p>
      </div>
      <div className="flex flex-col gap-4">
        <Prereqs runner={ctx?.runner} model={ctx?.model} host={ctx?.host} />
        <div
          className={cn("flex flex-col gap-4", !ctx && !current && "opacity-50")}
          inert={!ctx && !current ? true : undefined}
        >
          <Card size="sm">
            <CardHeader>
              <CardTitle>{t("deploy.details.title")}</CardTitle>
              <CardDescription>
                {rich(needsKey(preset) ? "deploy.details.description" : "deploy.details.descriptionMemory", {
                  cloud: <span className="font-mono">{host?.name ?? t("deploy.key.cloudFallback")}</span>,
                })}
              </CardDescription>
            </CardHeader>
            <CardContent className="flex flex-col gap-4">
              {!needsKey(preset) ? (
                <p className="text-sm text-muted-foreground">{t("deploy.key.none")}</p>
              ) : (
                <div className="flex flex-col gap-3">
                  <div className="flex flex-col gap-2 sm:flex-row sm:items-end">
                    <div className="grid min-w-0 flex-1 gap-2">
                      <Label htmlFor="upstream-key">{t("deploy.key.label")}</Label>
                      <Input
                        id="upstream-key"
                        type="password"
                        autoComplete="off"
                        placeholder={draft.keyFingerprint ? t("deploy.key.stored") : "sk-…"}
                        value={keyText}
                        disabled={!!draft.keyFingerprint || storing || locked}
                        onChange={(e) => setKeyText(e.target.value)}
                        onKeyDown={(e) => e.key === "Enter" && void storeKey()}
                      />
                    </div>
                    <Button
                      onClick={() => void storeKey()}
                      disabled={!!draft.keyFingerprint || storing || !keyText.trim() || locked}
                    >
                      {storing ? <Loader2Icon className="animate-spin" /> : <KeyIcon />}
                      {t(draft.keyFingerprint ? "common.stored" : "common.store")}
                    </Button>
                  </div>
                  {draft.keyFingerprint && (
                    <p className="inline-flex items-center gap-1.5 font-mono text-xs text-emerald-600 dark:text-emerald-400">
                      <CheckIcon className="size-3.5" />
                      {t("deploy.key.line", { fp: draft.keyFingerprint })}
                    </p>
                  )}
                </div>
              )}
              <div className="grid gap-2">
                <Label htmlFor="deploy-preset">{t("deploy.details.preset")}</Label>
                <div className="flex flex-col gap-2 sm:flex-row sm:items-center">
                  <Select
                    value={shown?.preset ?? preset ?? ""}
                    onValueChange={(p) =>
                      set({
                        preset: p,
                        pricing: null,
                        id: "",
                        symbol: "",
                        labels: "",
                        models: "",
                        upstreamUrl: "",
                        rate: 1 / (PRESETS[p]?.perCredit ?? 1),
                      })
                    }
                    disabled={locked}
                  >
                    <SelectTrigger id="deploy-preset" className="w-full font-mono sm:w-64">
                      <SelectValue placeholder={t("deploy.details.presetPending")} />
                    </SelectTrigger>
                    <SelectContent>
                      {PRESET_NAMES.map((p) => (
                        <SelectItem key={p} value={p} className="font-mono">
                          {p}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  {draft.detected && preset === draft.detected && (
                    <Badge variant="secondary" className="w-fit">
                      {PRESETS[draft.detected]?.shape
                        ? t("deploy.details.detected", { shape: PRESETS[draft.detected]!.shape! })
                        : t("deploy.details.detectedNone")}
                    </Badge>
                  )}
                  {draft.detected && preset !== draft.detected && (
                    <span className="text-xs text-muted-foreground">
                      {rich("deploy.details.changed", { preset: <span className="font-mono">{draft.detected}</span> })}
                    </span>
                  )}
                </div>
              </div>
              <div className="grid gap-3 sm:grid-cols-2">
                <Field
                  id="deploy-id"
                  label={t("deploy.details.id")}
                  value={shown?.id ?? details?.id ?? ""}
                  placeholder={PRESETS["openai-compatible"]!.suggest.id}
                  disabled={!details || locked}
                  problem={problems.id}
                  hint={
                    details?.id &&
                    rich("deploy.details.idHint", { name: <span className="font-mono wrap-anywhere">{fullName}</span> })
                  }
                  onChange={(v) => set({ id: v.trim() })}
                />
                <Field
                  id="deploy-symbol"
                  label={t("deploy.details.symbol")}
                  value={shown?.symbol ?? details?.symbol ?? ""}
                  placeholder={PRESETS["openai-compatible"]!.suggest.symbol}
                  disabled={!details || locked}
                  problem={problems.symbol}
                  onChange={(v) => set({ symbol: v.trim() })}
                />
                <Field
                  id="deploy-label"
                  label={t("deploy.details.label")}
                  value={shown ? shown.labels.join(", ") : (details?.labels ?? "")}
                  placeholder={t(PRESETS["openai-compatible"]!.suggest.label)}
                  disabled={!details || locked}
                  problem={problems.labels}
                  hint={
                    <span className="flex flex-col gap-1">
                      {details && labelsOf(details.labels).length > 0 && (
                        <span className="flex flex-wrap gap-1">
                          {labelsOf(details.labels).map((l, i) => (
                            <LabelTag key={i}>{l}</LabelTag>
                          ))}
                        </span>
                      )}
                      {t("deploy.details.labelHint")}
                    </span>
                  }
                  onChange={(v) => set({ labels: v })}
                />
                <div className="grid min-w-0 content-start gap-2">
                  <Label htmlFor="deploy-icon">{t("deploy.details.icon")}</Label>
                  <Select
                    value={details?.icon && isIcon(details.icon) ? details.icon : "default"}
                    onValueChange={(v) => set({ icon: v === "default" ? "" : v })}
                    disabled={!details || locked}
                  >
                    <SelectTrigger id="deploy-icon" className="w-full">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="default">
                        <ServiceIcon icon={preset ? KIND_ICON[spec?.kinds[0] ?? "tool"] : "wrench"} />
                        {t("deploy.details.iconDefault")}
                      </SelectItem>
                      {Object.keys(ICONS).map((i) => (
                        <SelectItem key={i} value={i} className="font-mono">
                          <ServiceIcon icon={i} />
                          {i}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <p className="text-xs text-muted-foreground">{t("deploy.details.iconHint")}</p>
                </div>
                {!handle?.exists && details && (
                  <Field
                    id="deploy-handle"
                    label={t("deploy.handle")}
                    value={draft.handle}
                    placeholder="steven"
                    disabled={locked}
                    problem={problems.handle}
                    hint={t("deploy.handleHint", { handle: draft.handle || "…", addr4 })}
                    onChange={(v) => set({ handle: v.trim().toLowerCase() })}
                  />
                )}
                {needsKey(preset) && details && (
                  <Field
                    id="deploy-upstream"
                    label={t("deploy.upstreamUrl")}
                    value={details.upstreamUrl}
                    disabled={locked}
                    problem={problems.upstreamUrl}
                    hint={t("deploy.upstreamUrlHint")}
                    onChange={(v) => set({ upstreamUrl: v.trim() })}
                  />
                )}
                {spec?.kinds.includes("model") && details && (
                  <Field
                    id="deploy-models"
                    label={t("deploy.models")}
                    value={details.models}
                    disabled={locked}
                    hint={t("deploy.modelsHint")}
                    onChange={(v) => set({ models: v })}
                  />
                )}
              </div>
              {details && <PricingEditor draft={details} set={set} disabled={locked} />}
              {details && <BatchField draft={details} set={set} disabled={locked} />}
              <div className="flex flex-col gap-3 border-t pt-4 sm:flex-row sm:items-center sm:justify-between">
                <p className="text-xs text-muted-foreground">
                  {t("deploy.deployNote", {
                    sigs: tt(
                      approveNeeded.length > 0
                        ? Y("sigs.approve", { n: approveNeeded.length, symbols: approveNeeded.join(", ") })
                        : Y("sigs.authorize"),
                    ),
                  })}
                </p>
                {current ? (
                  <span className="inline-flex shrink-0 items-center gap-1.5 text-sm text-emerald-600 dark:text-emerald-400">
                    <CheckIcon className="size-4" />
                    {t("deploy.started")}
                  </span>
                ) : (
                  <Button className="shrink-0" onClick={() => void deploy()} disabled={!ready}>
                    {capState === "waiting" ? <Loader2Icon className="animate-spin" /> : <RocketIcon />}
                    {t("deploy.deploy")}
                  </Button>
                )}
              </div>
              {shortfalls.length > 0 && (
                <div className="flex flex-col gap-1 text-xs text-amber-700 dark:text-amber-400">
                  <p>{t("deploy.short.title")}</p>
                  <ul className="flex flex-col gap-0.5">
                    {shortfalls.map((tok) => (
                      <li key={tok}>
                        {rich("deploy.short.line", {
                          token: <span className="font-mono">{tok}</span>,
                          balance: n(balances[tok] ?? 0),
                          cap: n(caps[tok]!),
                          services: (
                            <a className="font-medium underline" href="#/services">
                              {t("deploy.short.services")}
                            </a>
                          ),
                          buy: (
                            <button type="button" className="font-medium underline" onClick={() => onBuy(tok)}>
                              {t("cell.buy", { token: tok })}
                            </button>
                          ),
                        })}
                      </li>
                    ))}
                  </ul>
                </div>
              )}
              {capState === "waiting" && !current && <Waiting n={approveNeeded.length + 1} className="text-xs" />}
              {capState === "rejected" && !current && <Rejected className="text-xs" />}
              {details && (
                <p className="text-xs text-muted-foreground">
                  {tt(batchText(details.batch))} · {capsText(caps)}
                </p>
              )}
            </CardContent>
          </Card>
          <RunPanel c={current} draft={draft} caps={caps} ctx={ctx} />
        </div>
      </div>
    </>
  );
}
